let overlay = null;
let lastFocused = null;
let currentTriggers = [];
let currentIndex = -1;

function getStrings() {
	return window.icarImageLightbox || {};
}

function getFocusableElements(container) {
	if (!container) {
		return [];
	}

	return [...container.querySelectorAll(
		'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
	)].filter((element) => !element.hidden && element.getAttribute('aria-hidden') !== 'true');
}

function applyLocalizedLabels(root) {
	const strings = getStrings();
	const dialog = root.querySelector('.image-lightbox__dialog');

	root.querySelectorAll('[data-image-lightbox-close]').forEach((button) => {
		if (strings.close) {
			button.setAttribute('aria-label', strings.close);
		}
	});

	if (dialog && strings.imagePreview) {
		dialog.setAttribute('aria-label', strings.imagePreview);
	}

	const prev = root.querySelector('[data-image-lightbox-prev]');
	const next = root.querySelector('[data-image-lightbox-next]');

	if (prev && strings.previousImage) {
		prev.setAttribute('aria-label', strings.previousImage);
	}

	if (next && strings.nextImage) {
		next.setAttribute('aria-label', strings.nextImage);
	}
}

function trapFocus(event) {
	if (!overlay || overlay.hidden || event.key !== 'Tab') {
		return;
	}

	const dialog = overlay.querySelector('.image-lightbox__dialog');
	const focusable = getFocusableElements(dialog);

	if (focusable.length === 0) {
		event.preventDefault();
		return;
	}

	const first = focusable[0];
	const last = focusable[focusable.length - 1];
	const active = document.activeElement;

	if (event.shiftKey && active === first) {
		event.preventDefault();
		last.focus();
		return;
	}

	if (!event.shiftKey && active === last) {
		event.preventDefault();
		first.focus();
	}
}

function ensureOverlay() {
	if (overlay) {
		applyLocalizedLabels(overlay);
		return overlay;
	}

	const strings = getStrings();

	overlay = document.createElement('div');
	overlay.className = 'image-lightbox';
	overlay.hidden = true;
	overlay.innerHTML = `
		<button type="button" class="image-lightbox__backdrop" data-image-lightbox-close aria-label="${strings.close || 'Close'}"></button>
		<div class="image-lightbox__dialog" role="dialog" aria-modal="true" aria-label="${strings.imagePreview || 'Image preview'}">
			<button type="button" class="image-lightbox__close" data-image-lightbox-close aria-label="${strings.close || 'Close'}"></button>
			<button type="button" class="image-lightbox__nav image-lightbox__nav--prev" data-image-lightbox-prev aria-label="${strings.previousImage || 'Previous image'}" hidden></button>
			<img class="image-lightbox__image" src="" alt="" />
			<button type="button" class="image-lightbox__nav image-lightbox__nav--next" data-image-lightbox-next aria-label="${strings.nextImage || 'Next image'}" hidden></button>
		</div>
	`;

	document.body.appendChild(overlay);

	overlay.addEventListener('click', (event) => {
		if (event.target.closest('[data-image-lightbox-close]')) {
			event.preventDefault();
			closeLightbox();
		}

		if (event.target.closest('[data-image-lightbox-prev]')) {
			event.preventDefault();
			showAtIndex(currentIndex - 1);
		}

		if (event.target.closest('[data-image-lightbox-next]')) {
			event.preventDefault();
			showAtIndex(currentIndex + 1);
		}
	});

	document.addEventListener('keydown', onKeydown);

	return overlay;
}

function onKeydown(event) {
	if (!overlay || overlay.hidden) {
		return;
	}

	trapFocus(event);

	if (event.key === 'Escape') {
		closeLightbox();
		return;
	}

	if (event.key === 'ArrowLeft') {
		showAtIndex(currentIndex - 1);
		return;
	}

	if (event.key === 'ArrowRight') {
		showAtIndex(currentIndex + 1);
	}
}

function updateNavButtons() {
	if (!overlay) {
		return;
	}

	const prev = overlay.querySelector('[data-image-lightbox-prev]');
	const next = overlay.querySelector('[data-image-lightbox-next]');
	const hasMultiple = currentTriggers.length > 1;

	if (prev) {
		prev.hidden = !hasMultiple || currentIndex <= 0;
	}

	if (next) {
		next.hidden = !hasMultiple || currentIndex >= currentTriggers.length - 1;
	}
}

function showAtIndex(index) {
	if (!overlay || index < 0 || index >= currentTriggers.length) {
		return;
	}

	const trigger = currentTriggers[index];
	const src = trigger.getAttribute('data-full-src');
	const caption = trigger.getAttribute('data-caption') || trigger.getAttribute('aria-label') || '';
	const image = overlay.querySelector('.image-lightbox__image');

	currentIndex = index;

	if (image && src) {
		image.src = src;
		image.alt = caption;
	}

	updateNavButtons();
}

export function openLightbox(trigger, triggers = []) {
	if (!trigger || trigger.disabled) {
		return;
	}

	const root = ensureOverlay();
	currentTriggers = triggers.length ? triggers : [trigger];
	currentIndex = Math.max(0, currentTriggers.indexOf(trigger));

	lastFocused = document.activeElement;
	root.hidden = false;
	document.documentElement.classList.add('image-lightbox-scroll-lock');
	showAtIndex(currentIndex);
	root.querySelector('.image-lightbox__close')?.focus();
}

export function closeLightbox() {
	if (!overlay || overlay.hidden) {
		return;
	}

	overlay.hidden = true;
	document.documentElement.classList.remove('image-lightbox-scroll-lock');

	const image = overlay.querySelector('.image-lightbox__image');

	if (image) {
		image.removeAttribute('src');
		image.alt = '';
	}

	currentTriggers = [];
	currentIndex = -1;

	if (lastFocused?.focus) {
		lastFocused.focus();
	}

	lastFocused = null;
}

export function initImageLightbox(container) {
	if (!container) {
		return;
	}

	const triggers = [...container.querySelectorAll('[data-image-lightbox]:not([disabled])')];

	triggers.forEach((trigger) => {
		if (trigger.dataset.lightboxBound === '1') {
			return;
		}

		trigger.dataset.lightboxBound = '1';

		trigger.addEventListener('click', (event) => {
			event.preventDefault();
			openLightbox(trigger, triggers);
		});
	});
}
